/**
 * Modals for editing the org-chart STRUCTURE (org_units + team placement).
 * Opened from the chart's "Edit structure" mode. Each modal owns its own
 * mutation + query invalidation (mirrors AddTeamModal), so the chart only
 * has to decide which modal is open and hand it the current data.
 */
import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { OrgUnitType } from '@lms/shared-types'
import { Modal } from '../../../primitives/Modal'
import { Button } from '../../../primitives/Button'
import { Combobox } from '../../../primitives/Combobox'
import { UnitTypeChip, UNIT_TYPE_LABEL } from '../UnitTypeChip'
import { createOrgUnit, deleteOrgUnit, updateOrgUnit } from '../../../../api/org-units'
import { setTeamUnit } from '../../../../api/admin-teams'

const UNIT_TYPES: { value: OrgUnitType; label: string }[] = [
  { value: 'company', label: 'Company' },
  { value: 'function', label: 'Function' },
  { value: 'department', label: 'Department' },
]

export interface UnitLite {
  id: string
  name: string
  unitType: OrgUnitType
}
export interface EmpLite {
  id: string
  name: string
  jobTitle: string
}

const fieldStyle: React.CSSProperties = {
  padding: '8px 10px',
  border: '1px solid var(--border)',
  borderRadius: 8,
  fontSize: 14,
  background: 'var(--surface)',
  color: 'var(--text)',
}
const labelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  fontSize: 12,
  color: 'var(--text-2)',
}

function errText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

// ── Unit create / edit ────────────────────────────────────────────────────

export interface UnitEditorTarget {
  id: string
  name: string
  unitType: OrgUnitType
  parentId: string | null
  headId: string | null
}

interface UnitEditorModalProps {
  open: boolean
  onClose: () => void
  mode: 'create' | 'edit'
  units: UnitLite[]
  employees: EmpLite[]
  /** edit mode: the unit being edited */
  target?: UnitEditorTarget | null
  /** create mode: preselected parent */
  createParentId?: string | null
}

export function UnitEditorModal({
  open,
  onClose,
  mode,
  units,
  employees,
  target,
  createParentId,
}: UnitEditorModalProps): JSX.Element {
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [unitType, setUnitType] = useState<OrgUnitType>('department')
  const [parentId, setParentId] = useState<string>('')
  const [headId, setHeadId] = useState<string>('')
  const [error, setError] = useState<string | null>(null)

  // (Re)initialise the form whenever the modal opens or its target changes.
  useEffect(() => {
    if (!open) return
    if (mode === 'edit' && target) {
      setName(target.name)
      setUnitType(target.unitType)
      setParentId(target.parentId ?? '')
      setHeadId(target.headId ?? '')
    } else {
      setName('')
      setUnitType('department')
      setParentId(createParentId ?? '')
      setHeadId('')
    }
    setError(null)
  }, [open, mode, target, createParentId])

  const parentOptions = units.filter((u) => u.id !== target?.id)

  const save = useMutation({
    mutationFn: async () => {
      const head = headId || null
      if (mode === 'create') {
        await createOrgUnit({
          parentId: parentId || null,
          unitType,
          name: name.trim(),
          headEmployeeId: head,
        })
      } else if (target) {
        await updateOrgUnit(target.id, {
          name: name.trim(),
          unitType,
          headEmployeeId: head,
          ...(parentId ? { parentId } : {}),
        })
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['hr', 'org-chart'] })
      onClose()
    },
    onError: (err) => setError(errText(err, 'Could not save unit.')),
  })

  const remove = useMutation({
    mutationFn: async () => {
      if (target) await deleteOrgUnit(target.id)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['hr', 'org-chart'] })
      onClose()
    },
    onError: (err) => setError(errText(err, 'Could not delete unit.')),
  })

  function handleSubmit(e: React.FormEvent<HTMLFormElement>): void {
    e.preventDefault()
    setError(null)
    if (name.trim().length === 0) {
      setError('Unit name is required.')
      return
    }
    if (mode === 'create' && !parentId) {
      setError('Pick a parent unit.')
      return
    }
    save.mutate()
  }

  const busy = save.isPending || remove.isPending

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={mode === 'create' ? 'Add org unit' : 'Edit org unit'}
      footer={
        <>
          {mode === 'edit' ? (
            <Button
              variant="destructive"
              type="button"
              data-testid="unit-editor-delete"
              loading={remove.isPending}
              onClick={() => {
                // eslint-disable-next-line no-alert
                if (window.confirm(`Delete “${target?.name}”?`)) remove.mutate()
              }}
              style={{ marginRight: 'auto' }}
            >
              Delete
            </Button>
          ) : null}
          <Button variant="secondary" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="unit-editor-form"
            loading={save.isPending}
            disabled={busy}
          >
            {mode === 'create' ? 'Create' : 'Save'}
          </Button>
        </>
      }
    >
      <form
        id="unit-editor-form"
        onSubmit={handleSubmit}
        style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
      >
        <label style={labelStyle}>
          <span>Name</span>
          <input
            data-testid="unit-editor-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            maxLength={120}
            style={fieldStyle}
          />
        </label>

        <label style={labelStyle}>
          <span>Type</span>
          <select
            data-testid="unit-editor-type"
            value={unitType}
            onChange={(e) => setUnitType(e.target.value as OrgUnitType)}
            style={fieldStyle}
          >
            {UNIT_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </label>

        <label style={labelStyle}>
          <span>Parent unit{mode === 'edit' ? ' (move)' : ''}</span>
          <Combobox
            inputTestId="unit-editor-parent"
            ariaLabel="Parent unit"
            placeholder="Search a unit…"
            emptyOptionLabel="— none (root) —"
            value={parentId}
            onChange={setParentId}
            options={parentOptions.map((u) => ({
              value: u.id,
              label: u.name,
              badge: <UnitTypeChip unitType={u.unitType} />,
            }))}
          />
        </label>

        <label style={labelStyle}>
          <span>Head (optional)</span>
          <Combobox
            inputTestId="unit-editor-head"
            ariaLabel="Head"
            placeholder="Search an employee…"
            emptyOptionLabel="— no head —"
            value={headId}
            onChange={setHeadId}
            options={employees.map((emp) => ({
              value: emp.id,
              label: emp.name,
              sublabel: emp.jobTitle,
            }))}
          />
        </label>

        {error ? (
          <p
            data-testid="unit-editor-error"
            style={{ fontSize: 13, color: 'var(--danger-fg)', margin: 0 }}
          >
            {error}
          </p>
        ) : null}
      </form>
    </Modal>
  )
}

// ── Move a team to a unit ───────────────────────────────────────────────────

interface TeamUnitModalProps {
  open: boolean
  onClose: () => void
  units: UnitLite[]
  team: { id: string; name: string; parentUnitId: string | null } | null
}

export function TeamUnitModal({
  open,
  onClose,
  units,
  team,
}: TeamUnitModalProps): JSX.Element {
  const queryClient = useQueryClient()
  const [unitId, setUnitId] = useState<string>('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setUnitId(team?.parentUnitId ?? '')
      setError(null)
    }
  }, [open, team])

  const save = useMutation({
    mutationFn: async () => {
      if (team) await setTeamUnit(team.id, unitId || null)
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['hr', 'org-chart'] }),
        queryClient.invalidateQueries({ queryKey: ['teams'] }),
      ])
      onClose()
    },
    onError: (err) => setError(errText(err, 'Could not move team.')),
  })

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={team ? `Move “${team.name}”` : 'Move team'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="team-unit-form"
            loading={save.isPending}
            data-testid="team-unit-save"
          >
            Save
          </Button>
        </>
      }
    >
      <form
        id="team-unit-form"
        onSubmit={(e) => {
          e.preventDefault()
          save.mutate()
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
      >
        <label style={labelStyle}>
          <span>Org unit</span>
          <select
            data-testid="team-unit-select"
            value={unitId}
            onChange={(e) => setUnitId(e.target.value)}
            style={fieldStyle}
          >
            <option value="">— Unassigned —</option>
            {units.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name} · {UNIT_TYPE_LABEL[u.unitType]}
              </option>
            ))}
          </select>
        </label>
        {error ? (
          <p style={{ fontSize: 13, color: 'var(--danger-fg)', margin: 0 }}>
            {error}
          </p>
        ) : null}
      </form>
    </Modal>
  )
}
